/**
 * Ability Context & Provider
 *
 * Provides CASL ability instance to the React tree.
 *
 * @layer features
 * @slice authorization
 * @segment model
 * @added DOC-0064 (2025-12-19)
 * @updated DOC-0095 (2025-12-26) - Migrated to FSD features/authorization
 */

import { createContext, useMemo, type ReactNode } from 'react';
import { createContextualCan } from '@casl/react';
import { createAbilityFromRules, defaultAbility } from '../lib/ability';
import { useAuth } from '@features/auth';
import type { AppAbility } from '@shared/types/ability';

/**
 * Ability context
 *
 * Defaults to empty ability for unauthenticated users.
 */
export const AbilityContext = createContext<AppAbility>(defaultAbility);

/**
 * CASL Can component wired to AbilityContext
 * @internal Use the Can component from ui/ segment instead
 */
export const CaslCan = createContextualCan(AbilityContext.Consumer);

interface AbilityProviderProps {
  children: ReactNode;
}

/**
 * Ability Provider Component
 *
 * Builds ability from serialized rules in AuthContext.
 */
export function AbilityProvider({ children }: AbilityProviderProps) {
  const { user, permissions } = useAuth();

  const ability = useMemo(() => {
    if (!user || !permissions || permissions.length === 0) {
      return defaultAbility;
    }

    return createAbilityFromRules(permissions);
  }, [user, permissions]);

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
}
